import { attributeValueDisplayLabel } from '@/shein-platform/utils/sheinAttributeTemplate';

const PATCH_FIELDS = ['weight', 'length', 'width', 'height', 'quantity', 'quantityUnit', 'packageType'];

export function skuPackageSizeKey(sku) {
  if (!sku) return '';
  if (sku.sizeIsCustom || sku.sizeExtraValue) {
    const raw = String(sku.sizeExtraValue || sku.sizeLabel || '').trim();
    return raw ? `custom:${raw}` : '';
  }
  if (sku.sizeValueId != null && String(sku.sizeValueId).trim() !== '') {
    return `id:${String(sku.sizeValueId)}`;
  }
  const label = String(sku.sizeLabel || '').trim();
  return label ? `label:${label}` : '';
}

export function skuPackageSizeLabel(sku, sizeAttr) {
  if (sku?.sizeIsCustom || sku?.sizeExtraValue) {
    return attributeValueDisplayLabel(sku.sizeLabel || sku.sizeExtraValue)
      || String(sku.sizeExtraValue || sku.sizeLabel || '').trim()
      || '自定义';
  }
  return attributeValueDisplayLabel(
    sizeAttr?.values?.find((v) => String(v.id) === String(sku?.sizeValueId)) || sku?.sizeLabel,
  ) || String(sku?.sizeLabel || sku?.sizeValueId || '').trim() || '—';
}

function seedFromSku(sku) {
  if (!sku || typeof sku !== 'object') return {};
  return {
    weight: String(sku.weight ?? '').trim(),
    length: String(sku.length ?? '').trim(),
    width: String(sku.width ?? '').trim(),
    height: String(sku.height ?? '').trim(),
    quantity: String(sku.quantity ?? '').trim(),
    quantityUnit: String(sku.quantityUnit || '1'),
    packageType: sku.packageType === 0 || sku.packageType === '0' ? '' : String(sku.packageType ?? ''),
  };
}

/** 从当前 SKU 行收集去重尺码，并带上该尺码已有包装值（取首次出现）。 */
export function collectUniquePackageSizes(skcList, sizeAttr) {
  const seen = new Map();
  (Array.isArray(skcList) ? skcList : []).forEach((skc) => {
    (skc?.skuRows || []).forEach((sku) => {
      const key = skuPackageSizeKey(sku);
      if (!key || seen.has(key)) return;
      seen.set(key, {
        key,
        label: skuPackageSizeLabel(sku, sizeAttr),
        seed: seedFromSku(sku),
      });
    });
  });
  return [...seen.values()];
}

export function packagePatchHasValue(patch) {
  if (!patch || typeof patch !== 'object') return false;
  return ['weight', 'length', 'width', 'height', 'quantity', 'packageType']
    .some((field) => String(patch[field] ?? '').trim() !== '');
}

export function packageBatchHasAnyValue(bySize) {
  return Object.values(bySize || {}).some((patch) => packagePatchHasValue(patch));
}

function applyPatchToSku(sku, patch) {
  if (!packagePatchHasValue(patch)) return sku;
  const next = { ...sku };
  PATCH_FIELDS.forEach((field) => {
    if (patch[field] == null) return;
    const val = String(patch[field]).trim();
    if (!val) return;
    next[field] = val;
  });
  return next;
}

/** 按尺码把包装字段写回所有颜色的对应 SKU。未填的尺码保持原值。 */
export function applyPackageBatchBySize(skcList, bySize) {
  const map = bySize && typeof bySize === 'object' ? bySize : {};
  return (Array.isArray(skcList) ? skcList : []).map((skc) => ({
    ...skc,
    skuRows: (skc?.skuRows || []).map((sku) => {
      const key = skuPackageSizeKey(sku);
      return applyPatchToSku(sku, key ? map[key] : null);
    }),
  }));
}

/** 包装尺寸 / 包装类型：同一套值写到全部 SKU，不按尺码区分。 */
export function applyPackageBatchToAllSkus(skcList, patch) {
  if (!packagePatchHasValue(patch)) {
    return Array.isArray(skcList) ? skcList : [];
  }
  return (Array.isArray(skcList) ? skcList : []).map((skc) => ({
    ...skc,
    skuRows: (skc?.skuRows || []).map((sku) => applyPatchToSku(sku, patch)),
  }));
}
